import { useState } from 'react'
import type { ReactNode } from 'react'
import { inputStyle } from './ui'
import { punktTitle, punktText } from './erhvervVilkaar'
import type { ErhvervPunkt } from './erhvervVilkaar'

const BLUE = '#4f46e5'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LibraryClause {
  key: string | null
  title: string | null
  content: string
  category: string
  source: 'builtin' | 'custom'
}

// ── Main component ────────────────────────────────────────────────────────────

export function ErhvervSectionVilkaar({ items, library, sectionNo, onChange, onSaveTemplate, banner, footer }: {
  items: ErhvervPunkt[]
  library: LibraryClause[]
  sectionNo: number
  onChange: (items: ErhvervPunkt[]) => void
  onSaveTemplate: (title: string, content: string) => void
  banner?: ReactNode   // rendered at the top of the left column (hints/warnings)
  footer?: ReactNode   // rendered below the composer (e.g. drift og skatter)
}) {
  const [search, setSearch] = useState('')
  const [composerText, setComposerText] = useState('')
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [confirmIdx, setConfirmIdx] = useState<number | null>(null)

  // ── Mutations ──────────────────────────────────────────────────────────────

  // Single mutation entry-point: applies next items list, closes the editor
  // (or sets it to newEditingIdx when a move needs to follow the card).
  const update = (next: ErhvervPunkt[], newEditingIdx: number | null = null) => {
    setEditingIdx(newEditingIdx)
    onChange(next)
  }

  const moveUp = (idx: number) => {
    if (idx === 0) return
    const next = [...items]
    ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
    const newIdx =
      editingIdx === idx ? idx - 1 :
      editingIdx === idx - 1 ? idx :
      editingIdx  // unrelated card stays open
    update(next, newIdx)
  }

  const moveDown = (idx: number) => {
    if (idx === items.length - 1) return
    const next = [...items]
    ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
    const newIdx =
      editingIdx === idx ? idx + 1 :
      editingIdx === idx + 1 ? idx :
      editingIdx  // unrelated card stays open
    update(next, newIdx)
  }

  const removeItem = (idx: number) => {
    const next = items.filter((_, i) => i !== idx)
    let newIdx: number | null = null
    if (editingIdx !== null && editingIdx !== idx) {
      newIdx = editingIdx > idx ? editingIdx - 1 : editingIdx
    }
    update(next, newIdx)
  }

  const addCustom = () => {
    const text = composerText.trim()
    if (!text) return
    update([...items, { kind: 'custom', localId: `c-${Date.now()}`, text }])
    setComposerText('')
  }

  // Toggle a builtin from the library panel
  const toggleBuiltin = (clause: LibraryClause) => {
    if (clause.key === null) return  // null-keyed rows are tenant customs; use insert path
    const idx = items.findIndex(
      it => it.kind === 'builtin' && it.key === clause.key
    )
    if (idx >= 0) {
      // Remove — even if it has overrideText
      update(items.filter((_, i) => i !== idx))
    } else {
      update([...items, {
        kind: 'builtin',
        key: clause.key,
        title: clause.title ?? '',
        baseText: clause.content,
      }])
    }
  }

  // Insert a library custom row (always appends, no toggle)
  const insertLibraryCustom = (clause: LibraryClause) => {
    update([...items, {
      kind: 'custom',
      // Only ever invoked from an onClick handler (never during render); flagged
      // because the function is passed through a click closure the compiler
      // can't trace as click-only.
      // eslint-disable-next-line react-hooks/purity
      localId: `lib-${Date.now()}`,
      text: clause.content,
    }])
  }

  // Start editing a card body
  const startEdit = (idx: number) => {
    const it = items[idx]
    if (it.kind === 'builtin' && it.overrideText === undefined) {
      // Builtin with no overrideText yet — show confirm first
      setConfirmIdx(idx)
    } else {
      setEditingIdx(idx)
    }
  }

  // Confirm the modal: copy baseText → overrideText and open editor
  const confirmEdit = () => {
    if (confirmIdx === null) return
    const it = items[confirmIdx]
    if (it.kind !== 'builtin') return
    const next = items.map((p, i) =>
      i === confirmIdx && p.kind === 'builtin'
        ? { ...p, overrideText: p.baseText }
        : p
    ) as ErhvervPunkt[]
    onChange(next)
    setEditingIdx(confirmIdx)
    setConfirmIdx(null)
  }

  const updateOverrideText = (idx: number, text: string) => {
    const next = items.map((p, i) =>
      i === idx && p.kind === 'builtin'
        ? { ...p, overrideText: text }
        : p
    ) as ErhvervPunkt[]
    onChange(next)
  }

  const updateCustomText = (idx: number, text: string) => {
    const next = items.map((p, i) =>
      i === idx && p.kind === 'custom'
        ? { ...p, text }
        : p
    ) as ErhvervPunkt[]
    onChange(next)
  }

  // ── Library filtering ──────────────────────────────────────────────────────

  const q = search.trim().toLowerCase()
  const filteredLibrary = q
    ? library.filter(c =>
        (c.title ?? '').toLowerCase().includes(q) ||
        c.content.toLowerCase().includes(q)
      )
    : library

  const libraryCustom = filteredLibrary.filter(c => c.source === 'custom')

  // Builtin rows grouped by category in first-appearance order
  const categoryOrder: string[] = []
  for (const c of filteredLibrary) {
    if (c.source === 'builtin' && !categoryOrder.includes(c.category)) {
      categoryOrder.push(c.category)
    }
  }

  const activeBuiltinKeys = new Set(
    items.filter(it => it.kind === 'builtin').map(it => (it as Extract<ErhvervPunkt, { kind: 'builtin' }>).key)
  )

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* Confirm-before-edit modal */}
      {confirmIdx !== null && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: '28px 24px',
            maxWidth: 420, width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
              Rediger vilkår
            </div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 24 }}>
              Er du sikker på at du vil redigere vilkåret? Standardteksten kopieres og kan herefter ændres frit.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmIdx(null)}
                style={{
                  fontSize: 14, padding: '9px 18px', borderRadius: 8,
                  background: 'white', border: '1px solid #e5e7eb', cursor: 'pointer', color: '#374151',
                }}
              >Annuller</button>
              <button
                onClick={confirmEdit}
                style={{
                  fontSize: 14, fontWeight: 600, padding: '9px 18px', borderRadius: 8,
                  background: BLUE, color: 'white', border: 'none', cursor: 'pointer',
                }}
              >Rediger vilkår</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Left: punkt cards ── */}
      <div style={{
        flex: 1, minWidth: 0, borderRight: '1px solid #e9eaeb',
        overflowY: 'auto', display: 'flex', flexDirection: 'column',
      }}>
        {/* Cards centered within the left column (maxWidth + margin:0 auto), so
            the full-bleed step keeps the same centered framing rhythm as the
            other steps while the library panel stays flush-right. */}
        <div style={{ padding: '20px 24px 80px', width: '100%', maxWidth: 820, margin: '0 auto' }}>

          {banner}

          {/* Cards list or empty state */}
          {items.length === 0 ? (
            <div style={{
              padding: '32px 24px', color: '#9ca3af', fontSize: 14,
              textAlign: 'center', border: '1px dashed #e5e7eb', borderRadius: 8,
            }}>
              Ingen vilkår tilføjet endnu — vælg fra biblioteket til højre, eller skriv dit eget nedenfor.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {items.map((it, idx) => {
                const isEditing = editingIdx === idx
                const title = punktTitle(it)
                const text = punktText(it)
                const label = `§${sectionNo}.${idx + 1}`

                return (
                  <div
                    key={it.kind === 'builtin' ? `b-${it.key}` : it.localId}
                    style={{
                      border: '1px solid #e8eaed', borderRadius: 8,
                      background: 'white', overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}
                  >
                    {/* Card header */}
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      padding: '10px 14px', borderBottom: '1px solid #f3f4f6',
                      background: '#fafafa', gap: 10,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginBottom: 2 }}>
                          {label}
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{title}</div>
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {/* Move up */}
                        <ErvBtn disabled={idx === 0} onClick={() => moveUp(idx)}>
                          <ChevronUpIcon />
                        </ErvBtn>
                        {/* Move down */}
                        <ErvBtn disabled={idx === items.length - 1} onClick={() => moveDown(idx)}>
                          <ChevronDownIcon />
                        </ErvBtn>
                        {/* Save as template */}
                        <ErvBtn
                          onClick={() => onSaveTemplate(punktTitle(it), punktText(it))}
                          title="Gem som skabelon"
                        >
                          <BookmarkIcon />
                        </ErvBtn>
                        {/* Delete */}
                        <ErvBtn danger onClick={() => removeItem(idx)}>
                          <TrashIcon />
                        </ErvBtn>
                      </div>
                    </div>

                    {/* Card body */}
                    {isEditing ? (
                      <div style={{ padding: '12px 16px' }}>
                        <textarea
                          style={{
                            ...inputStyle,
                            minHeight: 120, resize: 'vertical',
                            fontFamily: 'inherit', fontSize: 13, lineHeight: 1.75,
                          }}
                          value={it.kind === 'builtin' ? (it.overrideText ?? it.baseText) : it.text}
                          onChange={e => {
                            if (it.kind === 'builtin') updateOverrideText(idx, e.target.value)
                            else updateCustomText(idx, e.target.value)
                          }}
                          autoFocus
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                          <button
                            onClick={() => setEditingIdx(null)}
                            style={{
                              fontSize: 13, fontWeight: 600, padding: '8px 18px', borderRadius: 8,
                              background: BLUE, color: 'white', border: 'none', cursor: 'pointer',
                            }}
                          >Gem ændringer</button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => startEdit(idx)}
                        title="Klik for at redigere"
                        style={{
                          padding: '14px 16px', fontSize: 13, color: '#374151',
                          lineHeight: 1.75, cursor: 'text', whiteSpace: 'pre-wrap',
                        }}
                      >
                        {text || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Tom — klik for at skrive.</span>}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Frifelt composer */}
          <div style={{
            marginTop: 24, border: '1.5px dashed #d1d5db', borderRadius: 8,
            padding: '16px 16px 14px', background: 'white',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
              Eget vilkår
            </div>
            <textarea
              style={{
                ...inputStyle, minHeight: 80, resize: 'vertical',
                fontFamily: 'inherit', fontSize: 13,
              }}
              placeholder="Skriv dit vilkår her…"
              value={composerText}
              onChange={e => setComposerText(e.target.value)}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                onClick={addCustom}
                disabled={!composerText.trim()}
                style={{
                  fontSize: 13, fontWeight: 600, padding: '8px 18px', borderRadius: 8,
                  background: composerText.trim() ? BLUE : '#e5e7eb',
                  color: composerText.trim() ? 'white' : '#9ca3af',
                  border: 'none',
                  cursor: composerText.trim() ? 'pointer' : 'default',
                  transition: 'background .15s, color .15s',
                }}
              >+ Tilføj</button>
            </div>
          </div>

          {footer}
        </div>
      </div>

      {/* ── Right: library panel ── */}
      {/* Stretches to the full height of the flex row (align-items:stretch is the
          default on the parent) and scrolls internally when taller than the
          viewport. No sticky / viewport-height cap — that left dead space below
          the panel when the left column scrolled (the flex row itself never
          scrolls; each column owns its own scroll). */}
      <div style={{
        width: 280, flexShrink: 0, background: '#f7f8fa', borderLeft: '1px solid #e8eaed',
        display: 'flex', flexDirection: 'column',
        minHeight: 0, overflowY: 'auto',
      }}>
        {/* Sticky header */}
        <div style={{
          position: 'sticky', top: 0, background: '#f7f8fa', zIndex: 1,
          borderBottom: '1px solid #e8eaed', padding: '14px 14px 10px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
            Standardvilkår
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5, marginBottom: 10 }}>
            Klik for at tilføje eller fjerne et vilkår.
          </div>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingLeft: 34, fontSize: 13 }}
              placeholder="Søg efter vilkår..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <svg
              style={{
                position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none',
              }}
              width="14" height="14" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
        </div>

        <div style={{ padding: '12px 14px 24px', flex: 1 }}>

          {/* Custom library rows — "Dine erhvervsvilkår" block */}
          {libraryCustom.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                padding: '4px 2px 6px',
                fontVariant: 'small-caps',
              }}>
                Dine erhvervsvilkår
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 7, overflow: 'hidden' }}>
                {libraryCustom.map((c, i) => (
                  <div
                    key={`lib-${c.title ?? ''}-${c.content.slice(0, 32)}`}
                    onClick={() => insertLibraryCustom(c)}
                    title="Klik for at indsætte som vilkår"
                    style={{
                      display: 'flex', gap: 10, padding: '10px 12px', cursor: 'pointer',
                      background: 'white',
                      borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                    }}
                  >
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2 }}>
                        {c.title || 'Eget vilkår'}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                        {c.content.slice(0, 80)}{c.content.length > 80 ? '…' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Builtin rows grouped by category */}
          {categoryOrder.map(cat => {
            const catItems = filteredLibrary.filter(
              c => c.source === 'builtin' && c.category === cat
            )
            if (!catItems.length) return null
            return (
              <div key={cat} style={{ marginBottom: 8 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: '#9ca3af',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  padding: '4px 2px 6px',
                  fontVariant: 'small-caps',
                }}>
                  {cat}
                </div>
                <div style={{
                  border: '1px solid #e5e7eb', borderRadius: 7, overflow: 'hidden',
                }}>
                  {catItems.map((c, i) => {
                    const active = c.key !== null && activeBuiltinKeys.has(c.key)
                    return (
                      <div
                        key={c.key ?? `builtin-${cat}-${i}`}
                        onClick={() => toggleBuiltin(c)}
                        style={{
                          display: 'flex', gap: 10, padding: '10px 12px', cursor: 'pointer',
                          background: active ? '#eff6ff' : 'white',
                          borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                        }}
                      >
                        {/* Checkbox indicator */}
                        <div style={{
                          width: 17, height: 17, borderRadius: 3, flexShrink: 0, marginTop: 2,
                          border: `2px solid ${active ? BLUE : '#d1d5db'}`,
                          background: active ? BLUE : 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {active && (
                            <svg width="9" height="9" viewBox="0 0 12 12"
                              fill="none" stroke="white" strokeWidth="2.5">
                              <polyline points="2 6 5 9 10 3"/>
                            </svg>
                          )}
                        </div>
                        <div>
                          <div style={{
                            fontSize: 13, fontWeight: 500,
                            color: active ? '#1e40af' : '#374151',
                            marginBottom: 2, lineHeight: 1.3,
                          }}>
                            {c.title}
                          </div>
                          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.4 }}>
                            {c.content.slice(0, 80)}{c.content.length > 80 ? '…' : ''}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {filteredLibrary.length === 0 && (
            <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '24px 0' }}>
              Ingen vilkår matcher søgningen.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Icon sub-components ───────────────────────────────────────────────────────

function ChevronUpIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="18 15 12 9 6 15"/>
    </svg>
  )
}

function ChevronDownIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14H6L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  )
}

function BookmarkIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
    </svg>
  )
}

function ErvBtn({ children, onClick, disabled, danger, title }: {
  children: ReactNode
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  title?: string
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #e5e7eb', borderRadius: 6,
        cursor: disabled ? 'default' : 'pointer',
        background: 'white',
        color: disabled ? '#e5e7eb' : danger ? '#dc2626' : '#6b7280',
      }}
    >
      {children}
    </button>
  )
}
